import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

import FixAddress from '@/components/FixAddress';
import OrderIcon from '@/components/OrderIcon/OrderIcon';
import ApiContext from '@/contexts/ApiContext';
import { useUser } from '@/contexts/UserContext';
import { useOrderView } from '@/contexts/OrderViewContext';
import getCommercialPanelOpenSaleLink from '@/helpers/get-commercial-panel-open-sale-link';
import openBryEnvelopeConfig from '@/helpers/openBryEnvelopeConfig';
import { hasFranchiseeActionAccess } from '@/helpers/orderPermissions';
import Loading from '@/Loading';
import FooterMessage from '@/orderDetails/FooterMessage';
import OrderDetailsBody from '@/orderDetails/OrderDetailsBody';
import OrderDetailsFooter from '@/orderDetails/OrderDetailsFooter';
import OrderDetailsHeader from '@/orderDetails/OrderDetailsHeader';
import { clearCurrentInspection } from '@/store/ducks/inspections';
import { clearCurrentOrder } from '@/store/ducks/orders';
import { showToast } from '@/store/ducks/toasts';
import { useOrder } from '@/hooks/useOrder';
import { useOrderMutations } from '@/hooks/useOrderMutations';
import { useQueryClient } from '@tanstack/react-query';

const OrderDetails = ({
  actionsMenuGeneratePDF,
  changePage,
  clientPanel,
  currentPageByOrderStatus,
  openAssignDevice,
  openAssignOrder,
  openEditPreviousInspection,
  openMergeOrder,
  orderEdit,
  readonly,
  refreshData,
  rejectOrder,
  updateOrderItem,
  requestInProgress,
}) => {
  const { orderId } = useParams();
  const intl = useIntl();
  const dispatch = useDispatch();
  const user = useUser();

  const { setActiveOrder } = useOrderView();
  const { data: order, isLoading, isError } = useOrder(orderId);
  const { updateStatus, updateOrder, syncEnvelope, resendEnvelope } = useOrderMutations(orderId);
  const queryClient = useQueryClient();

  const [newStatus, setNewStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [address, setAddress] = useState(undefined);
  const [footerErrorMessage, setFooterErrorMessage] = useState(null);
  const [bryConfigLoading, setBryConfigLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState('');

  // Sync with OrderViewContext
  useEffect(() => {
    if (order) {
      setActiveOrder(order);
    }
    return () => {
      setActiveOrder(prevActiveOrder => {
        if (prevActiveOrder && String(prevActiveOrder.id) === String(orderId)) {
          return null;
        }
        return prevActiveOrder;
      });
    };
  }, [order, orderId, setActiveOrder]);

  // Sync Current Page and Internal state when order data changes
  useEffect(() => {
    if (order && currentPageByOrderStatus) {
      setCurrentPage(currentPageByOrderStatus.get(order.status));
    }
  }, [order, currentPageByOrderStatus]);

  // Handle Error Toast
  useEffect(() => {
    if (isError) {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'ORDER_LOAD_ERROR_MESSAGE' }),
          duration: 5000,
        })
      );
    }
  }, [isError, dispatch, intl]);

  // Initial Cleanup and state reset when orderId changes
  useEffect(() => {
    dispatch(clearCurrentOrder());
    dispatch(clearCurrentInspection());
    setNewStatus(null);
    setMessage('');
    setAddress(undefined);
    setFooterErrorMessage(null);
  }, [dispatch, orderId]);

  const requestOrderDetails = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['order', orderId] });
  }, [queryClient, orderId]);

  const marketPlace = useCallback(
    (type, marketplaceData) => {
      if (type === 'DOCUSIGN_ACCEPTED' && marketplaceData) {
        marketplaceData.form.status = 'sent';
        updateOrder.mutate(
          { type: marketplaceData.type, ...marketplaceData.form },
          {
            onSuccess: () => {
              if (refreshData) refreshData();
              if (changePage) changePage('orders_list');
            },
          }
        );
      }
    },
    [updateOrder, refreshData, changePage]
  );

  const changeOrderStatus = useCallback(
    action => {
      if (action === null) {
        setNewStatus(null);
        setMessage('');
        setFooterErrorMessage(null);
        return;
      }

      if (newStatus === null) {
        setNewStatus(action);
        return;
      }

      if (newStatus === 'APPROVE') {
        updateStatus.mutate(
          { action: 'approve', message, address: address || undefined },
          {
            onSuccess: resData => {
              if (updateOrderItem) updateOrderItem(resData);
              setNewStatus(null);
              setMessage('');
              setFooterErrorMessage(null);
            },
          }
        );
      } else if (newStatus === 'SEND') {
        updateStatus.mutate(
          { action: 'send' },
          {
            onSuccess: resData => {
              if (updateOrderItem) updateOrderItem(resData);
              setNewStatus(null);
              setMessage('');
              setFooterErrorMessage(null);
              if (refreshData) refreshData();
            },
            onError: error => {
              let m = null;
              switch (error.response?.data?.error) {
                case 'INVALID_CREDITS_ZERO':
                  m = { id: 'MESSAGE.INVALID_CREDITS_ZERO' };
                  break;
                case 'OFFICIAL_INSPECTION_NULL':
                  m = { id: 'MESSAGE.OFFICIAL_REQUIRED' };
                  break;
                case 'SINGLE_ORDER_PURSCHASE_IS_OPEN':
                  m = { id: 'MESSAGE.SINGLE_ORDER_PURSCHASE_IS_OPEN', saleId: error.response.data.sale_id };
                  break;
                default:
                  m = { id: 'MESSAGE.TRANSITION_FAILED' };
                  break;
              }
              setFooterErrorMessage(m);
            },
          }
        );
      } else if (newStatus === 'REJECT') {
        if (rejectOrder) {
          rejectOrder(message)
            .then(() => {
              setNewStatus(null);
              setMessage('');
              setFooterErrorMessage(null);
            })
            .catch(() => {});
        }
      }
    },
    [newStatus, message, address, updateStatus, updateOrderItem, refreshData, rejectOrder]
  );

  const resendEnvelopeAction = useCallback(
    async evt => {
      const target = evt.currentTarget;
      if (target.disabled) return;
      resendEnvelope.mutate(undefined, {
        onSuccess: () => {
          target.disabled = true;
        },
      });
    },
    [resendEnvelope]
  );

  const resyncEnvelopeAction = useCallback(
    async evt => {
      const target = evt.currentTarget;
      if (target.disabled) return;
      syncEnvelope.mutate(undefined, {
        onSuccess: () => {
          target.disabled = true;
        },
      });
    },
    [syncEnvelope]
  );

  const saveMedia = useCallback(async () => {
    // Placeholder for media logic
  }, []);

  const configureBryEnvelope = useCallback(async () => {
    if (!order?.id) return;
    setBryConfigLoading(true);
    try {
      await openBryEnvelopeConfig({ orderId: order.id, intl, dispatchToast: dispatch });
    } finally {
      setBryConfigLoading(false);
    }
  }, [order?.id, intl, dispatch]);

  const footerMessage = useMemo(() => {
    if (!order) return null;
    const canAccessActions = hasFranchiseeActionAccess(user, order);

    if (!canAccessActions && !clientPanel) return null;

    if (footerErrorMessage !== null) {
      let values = null;
      if (footerErrorMessage.id === 'MESSAGE.SINGLE_ORDER_PURSCHASE_IS_OPEN' && footerErrorMessage.saleId) {
        values = {
          saleId: (
            <a
              href={getCommercialPanelOpenSaleLink(footerErrorMessage.saleId)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#5882C1] underline"
            >
              {footerErrorMessage.saleId}
            </a>
          ),
        };
      }
      return (
        <p className="text-red-600 w-[80%] mx-auto mt-4 text-center">
          <small>
            <FormattedMessage id={footerErrorMessage.id} values={values} />
          </small>
        </p>
      );
    }

    if (newStatus === 'REJECT') {
      return <FooterMessage value={message} onChange={setMessage} newStatus={newStatus} />;
    }

    if (newStatus === 'APPROVE') {
      const { address_validation: addressValidation } = order;
      const elements = [<FooterMessage value={message} onChange={setMessage} newStatus={newStatus} key="msg" />];
      if (
        user.features?.includes('SCHEDULE') &&
        (addressValidation?.state === 'invalid' ||
          addressValidation?.city === 'invalid' ||
          addressValidation?.neighborhood === 'invalid')
      ) {
        elements.unshift(<FixAddress orderData={order} onChange={setAddress} key="fix" />);
      }
      return <div className="w-full">{elements}</div>;
    }

    return null;
  }, [user, order, clientPanel, footerErrorMessage, newStatus, message]);

  if (isLoading) return <Loading clientPanel={clientPanel} />;

  if (isError || !order) {
    const errorMsg = intl.formatMessage({ id: 'ORDER_LOAD_ERROR_MESSAGE' });
    return (
      <div className="flex flex-col flex-1 bg-white min-h-[400px] mx-3 rounded-sm justify-center items-center py-[10vh] px-4 font-['Open_Sans']">
        <AlertTriangle size={60} className="text-[#EF4444] fill-[#EF4444] stroke-white mb-4" />
        <p className="text-[20px] font-normal text-center text-[#2B313C]">{errorMsg}</p>
      </div>
    );
  }

  const isArchived = order.is_archived && user.authRole === 'ROLE_FRANCHISEE';
  const apiStatus =
    updateStatus.isPending || requestInProgress
      ? 'WORKING'
      : syncEnvelope.isPending
      ? 'WORKING_RESYNC'
      : resendEnvelope.isPending
      ? 'WORKING_RESEND'
      : 'IDLE';

  return (
    <ApiContext.Provider value={{ status: apiStatus }}>
      <div className="flex flex-col flex-1 bg-white min-h-full mx-3 rounded-sm">
        <div className="mt-0 pt-2 mb-4 min-h-[calc(100%-4px)] bg-white shadow-none">
          <div className="flex flex-col w-full">
            <div className="px-6 flex flex-col">
              <OrderDetailsHeader
                data={order}
                isArchived={isArchived}
                inspectionIcon={<OrderIcon orderType={order.type} fontSize="24px" />}
              />

              <OrderDetailsBody
                data={order}
                currentPage={currentPage}
                refreshHandler={requestOrderDetails}
                updateOrderItem={updateOrderItem}
                openEditPreviousInspection={openEditPreviousInspection}
              />

              {!isArchived &&
                (hasFranchiseeActionAccess(user, order) || clientPanel) && (
                  <OrderDetailsFooter
                    data={order}
                    address={address}
                    changePage={changePage}
                    clientPanel={clientPanel}
                    changeOrderStatus={changeOrderStatus}
                    openAssignDevice={openAssignDevice}
                    openAssignOrder={openAssignOrder}
                    openMergeOrder={openMergeOrder}
                    currentPage={currentPage}
                    orderStatus={order.status}
                    orderEdit={orderEdit}
                    resendEnvelope={resendEnvelopeAction}
                    resyncEnvelope={resyncEnvelopeAction}
                    resendSuccess={resendEnvelope.isSuccess}
                    resyncSuccess={syncEnvelope.isSuccess}
                    newStatus={newStatus}
                    backToOrder={requestOrderDetails}
                    readonly={readonly}
                    marketPlace={marketPlace}
                    requestInProgress={updateStatus.isPending || resendEnvelope.isPending || syncEnvelope.isPending}
                    saveMedia={saveMedia}
                    medias={order.medias || []}
                    configureBryEnvelope={configureBryEnvelope}
                    bryConfigLoading={bryConfigLoading}
                  >
                    {footerMessage}
                  </OrderDetailsFooter>
                )}
            </div>

            {isArchived && (
              <div className="flex justify-center items-center w-full p-[10px] border-t border-[#E9E9E9] bg-[#F5F5F5] text-[13px] text-[#858585]">
                <FormattedMessage id="ORDER_DETAILS_ARCHIVED_MESSAGE" />
              </div>
            )}
          </div>
        </div>
      </div>
    </ApiContext.Provider>
  );
};

export default OrderDetails;
